import React, { useEffect, useMemo, useState } from "react";
import API from "./api";
import { getPolicySummary } from "./rbac";

const BRAND = {
  red: "#D73C2E",
  black: "#252935",
  mix: "#B9343A",
  redSoft: "#fff2ef",
  redSoftAlt: "#fff7f5",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  shadow: "rgba(37,41,53,0.08)",
  muted: "#6b6f7d",
  mutedSoft: "#8b90a0",
  green: "#1f8f5f",
  greenSoft: "#edf9f2",
  amber: "#b7791f",
  amberSoft: "#fff8e7",
  panel: "#fffdfc",
};

const EMPTY_REPORT = {
  overview: {
    total_recommendations: 0,
    unique_recommenders: 0,
    unique_source_profiles: 0,
    unique_target_profiles: 0,
    shared_recommendations: 0,
    viewed_recommendations: 0,
    call_recommendations: 0,
    interested_recommendations: 0,
    meeting_planned_recommendations: 0,
    meeting_done_recommendations: 0,
    matched_recommendations: 0,
    rejected_recommendations: 0,
    cancelled_recommendations: 0,
    unmatched_recommendations: 0,
    active_recommendations: 0,
    meeting_recommendations: 0,
  },
  staff: [],
  profiles: [],
  profile_options: [],
  target_breakdowns: [],
  activity: [],
  filters: {
    recommended_by: null,
    from_profile_id: null,
    result_bucket: "all",
    recommendation_status: "all",
    date_from: null,
    date_to: null,
    q: null,
    limit: 120,
  },
  policy: {
    max_results: null,
  },
};

const RECOMMENDATION_STATUS_OPTIONS = [
  { value: "all", label: "All stages" },
  { value: "shared", label: "Shared" },
  { value: "viewed", label: "Viewed" },
  { value: "call", label: "Call" },
  { value: "interested", label: "Interested" },
  { value: "meeting_planned", label: "Meeting Planned" },
  { value: "meeting_done", label: "Meeting Done" },
  { value: "matched", label: "Matched" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const PROFILE_STAGE_METRICS = [
  { key: "shared", label: "Shared", field: "shared_recommendations" },
  { key: "viewed", label: "Viewed", field: "viewed_recommendations" },
  { key: "call", label: "Call", field: "call_recommendations" },
  { key: "interested", label: "Interested", field: "interested_recommendations" },
  {
    key: "meeting_planned",
    label: "Meeting Planned",
    field: "meeting_planned_recommendations",
  },
  {
    key: "meeting_done",
    label: "Meeting Done",
    field: "meeting_done_recommendations",
  },
  { key: "rejected", label: "Rejected", field: "rejected_recommendations" },
  { key: "cancelled", label: "Cancelled", field: "cancelled_recommendations" },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value)}%`;
}

function formatStatusLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Unknown";
  return normalized
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function classifyRecommendationStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "matched") return "matched";
  if (normalized === "rejected" || normalized === "cancelled") return "unmatched";
  return "active";
}

function statusPillStyle(status) {
  const bucket = classifyRecommendationStatus(status);
  if (bucket === "matched") {
    return {
      background: BRAND.greenSoft,
      color: BRAND.green,
      borderColor: "rgba(31,143,95,0.18)",
    };
  }
  if (bucket === "unmatched") {
    return {
      background: BRAND.redSoft,
      color: BRAND.red,
      borderColor: BRAND.borderStrong,
    };
  }
  return {
    background: BRAND.amberSoft,
    color: BRAND.amber,
    borderColor: "rgba(183,121,31,0.18)",
  };
}

function buildProfileLabel(profileNo, profileName) {
  const name = String(profileName || "").trim();
  if (profileNo && name) return `#${profileNo} - ${name}`;
  if (profileNo) return `#${profileNo}`;
  return name || "Unknown profile";
}

function formatContactSideLabel(contactSide, sourceProfile, targetProfile) {
  const rawSide = String(contactSide || "").trim();
  const normalized = rawSide.toLowerCase();
  const sourceProfileId = String(sourceProfile?.profileId || "").trim();
  const targetProfileId = String(targetProfile?.profileId || "").trim();
  if (!normalized) return "";
  if (normalized === "profile_1" || (sourceProfileId && rawSide === sourceProfileId)) {
    return buildProfileLabel(sourceProfile?.profileNo, sourceProfile?.profileName);
  }
  if (normalized === "profile_2" || (targetProfileId && rawSide === targetProfileId)) {
    return buildProfileLabel(targetProfile?.profileNo, targetProfile?.profileName);
  }
  if (normalized === "both") {
    return `${buildProfileLabel(sourceProfile?.profileNo, sourceProfile?.profileName)} + ${buildProfileLabel(targetProfile?.profileNo, targetProfile?.profileName)}`;
  }
  if (normalized === "internal") return "Admin Note (Internal)";
  return formatStatusLabel(contactSide);
}

function MetricCard({ label, value, hint }) {
  return (
    <div style={S.metricCard}>
      <div style={S.metricLabel}>{label}</div>
      <div style={S.metricValue}>{value}</div>
      <div style={S.metricHint}>{hint}</div>
    </div>
  );
}

function buildStageDetailEntries(activity, selectedProfile, selectedTargetId, stageKey) {
  const profileId = String(selectedProfile?.profile_id || "");
  const targetId = String(selectedTargetId || "all");
  if (!profileId) return [];

  const entries = (Array.isArray(activity) ? activity : []).flatMap((item) => {
    if (String(item?.from_profile_id || "") !== profileId) return [];
    if (targetId !== "all" && String(item?.to_profile_id || "") !== targetId) return [];

    const base = {
      recommendationId: item.id,
      from_profile_id: item.from_profile_id,
      from_profile_no: item.from_profile_no,
      from_profile_name: item.from_profile_name,
      to_profile_id: item.to_profile_id,
      to_profile_no: item.to_profile_no,
      to_profile_name: item.to_profile_name,
      recommendation_created_at: item.created_at,
    };

    if (stageKey === "shared") {
      return [
        {
          id: `recommendation-${item.id}`,
          status: "shared",
          timestamp: item.created_at,
          actor: item.recommended_by_email || "Unknown user",
          source: "recommendation",
          note: item.note_content || "No recommendation note captured.",
          contact_side: null,
          ...base,
        },
      ];
    }

    return (Array.isArray(item.outcomes) ? item.outcomes : [])
      .filter(
        (outcome) =>
          String(outcome?.match_status || "").trim().toLowerCase() === stageKey
      )
      .map((outcome) => ({
        id: `outcome-${outcome.id}`,
        status: outcome.match_status || stageKey,
        timestamp: outcome.matched_at || outcome.created_at,
        actor: outcome.matched_by_email || "Unknown user",
        source: outcome.outcome_source || "manual",
        note:
          outcome.outcome_notes ||
          outcome.note_content ||
          "No outcome note captured.",
        contact_side: outcome.contact_side || null,
        ...base,
      }));
  });

  return entries.sort((a, b) => {
    const left = new Date(b.timestamp || b.recommendation_created_at || 0).getTime();
    const right = new Date(a.timestamp || a.recommendation_created_at || 0).getTime();
    return left - right;
  });
}

function GhostAction({ children, onClick }) {
  return (
    <button type="button" style={S.ghostAction} onClick={onClick}>
      {children}
    </button>
  );
}

function HistoryModal({
  open,
  title,
  subtitle,
  items,
  loading,
  error,
  onClose,
  canViewProfile,
  onViewProfile,
}) {
  if (!open) return null;

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <div style={S.modalEyebrow}>History</div>
            <h3 style={S.modalTitle}>{title}</h3>
            {subtitle ? <div style={S.modalSub}>{subtitle}</div> : null}
          </div>
          <button type="button" style={S.modalClose} onClick={onClose}>
            {"\u00D7"}
          </button>
        </div>

        {loading ? (
          <div style={S.modalState}>Loading recommendation history...</div>
        ) : error ? (
          <div style={S.modalError}>{error}</div>
        ) : items.length === 0 ? (
          <div style={S.modalState}>No recommendation history found for this selection.</div>
        ) : (
          <div style={S.modalList}>
            {items.map((item) => {
              const badgeStyle = statusPillStyle(item.status);
              const outcomes = Array.isArray(item.outcomes) ? item.outcomes : [];
              return (
                <article key={item.id} style={S.modalItem}>
                  <div style={S.modalItemHead}>
                    <div style={S.modalProfileRoute}>
                      <button
                        type="button"
                        style={canViewProfile ? S.profileBtn : S.profileText}
                        onClick={() =>
                          canViewProfile && onViewProfile && onViewProfile(item.from_profile_id)
                        }
                        disabled={!canViewProfile || !onViewProfile}
                      >
                        {buildProfileLabel(item.from_profile_no, item.from_profile_name)}
                      </button>
                      <span style={S.arrow}>{"\u2192"}</span>
                      <button
                        type="button"
                        style={canViewProfile ? S.profileBtn : S.profileText}
                        onClick={() =>
                          canViewProfile && onViewProfile && onViewProfile(item.to_profile_id)
                        }
                        disabled={!canViewProfile || !onViewProfile}
                      >
                        {buildProfileLabel(item.to_profile_no, item.to_profile_name)}
                      </button>
                    </div>
                    <span
                      style={{
                        ...S.statusPill,
                        background: badgeStyle.background,
                        color: badgeStyle.color,
                        borderColor: badgeStyle.borderColor,
                      }}
                    >
                      {formatStatusLabel(item.status)}
                    </span>
                  </div>

                  <div style={S.modalMeta}>
                    <span>{item.recommended_by_email}</span>
                    <span>{formatDateTime(item.created_at)}</span>
                  </div>

                  <div style={S.notePanel}>
                    <div style={S.noteLabel}>Recommendation note</div>
                    <div style={S.noteValue}>
                      {item.note_content || "No recommendation note captured."}
                    </div>
                  </div>

                  <div style={S.historyBlock}>
                    <div style={S.historyTitle}>Outcome history</div>
                    {outcomes.length === 0 ? (
                      <div style={S.historyEmpty}>No outcomes logged yet.</div>
                    ) : (
                      outcomes.map((outcome) => (
                        <div key={outcome.id} style={S.historyRow}>
                          <div style={S.historyTop}>
                            <div style={S.historyStatusWrap}>
                              <span style={S.historyStatus}>
                                {formatStatusLabel(outcome.match_status)}
                              </span>
                              {outcome.contact_side ? (
                                <span style={S.historySidePill}>
                                  {formatContactSideLabel(
                                    outcome.contact_side,
                                    {
                                      profileId: item.from_profile_id,
                                      profileNo: item.from_profile_no,
                                      profileName: item.from_profile_name,
                                    },
                                    {
                                      profileId: item.to_profile_id,
                                      profileNo: item.to_profile_no,
                                      profileName: item.to_profile_name,
                                    }
                                  )}
                                </span>
                              ) : null}
                            </div>
                            <span style={S.historyDate}>
                              {formatDateTime(outcome.matched_at || outcome.created_at)}
                            </span>
                          </div>
                          <div style={S.historyByline}>
                            {outcome.matched_by_email || "Unknown user"} {"\u00B7"}{" "}
                            {outcome.outcome_source || "manual"}
                          </div>
                          {(outcome.outcome_notes || outcome.note_content) && (
                            <div style={S.historyNote}>
                              {outcome.outcome_notes || outcome.note_content}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StageDetailsModal({
  open,
  title,
  subtitle,
  items,
  onClose,
  canViewProfile,
  onViewProfile,
}) {
  if (!open) return null;

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modalCard} onClick={(event) => event.stopPropagation()}>
        <div style={S.modalHead}>
          <div>
            <div style={S.modalEyebrow}>Stage details</div>
            <h3 style={S.modalTitle}>{title}</h3>
            {subtitle ? <div style={S.modalSub}>{subtitle}</div> : null}
          </div>
          <button type="button" style={S.modalClose} onClick={onClose}>
            {"\u00D7"}
          </button>
        </div>

        {items.length === 0 ? (
          <div style={S.modalState}>No entries found for this stage.</div>
        ) : (
          <div style={S.modalList}>
            {items.map((item) => {
              const badgeStyle = statusPillStyle(item.status);
              return (
                <article key={item.id} style={S.modalItem}>
                  <div style={S.modalItemHead}>
                    <div style={S.modalProfileRoute}>
                      <button
                        type="button"
                        style={canViewProfile ? S.profileBtn : S.profileText}
                        onClick={() =>
                          canViewProfile &&
                          onViewProfile &&
                          onViewProfile(item.from_profile_id)
                        }
                        disabled={!canViewProfile || !onViewProfile}
                      >
                        {buildProfileLabel(item.from_profile_no, item.from_profile_name)}
                      </button>
                      <span style={S.arrow}>{"\u2192"}</span>
                      <button
                        type="button"
                        style={canViewProfile ? S.profileBtn : S.profileText}
                        onClick={() =>
                          canViewProfile &&
                          onViewProfile &&
                          onViewProfile(item.to_profile_id)
                        }
                        disabled={!canViewProfile || !onViewProfile}
                      >
                        {buildProfileLabel(item.to_profile_no, item.to_profile_name)}
                      </button>
                    </div>
                    <span
                      style={{
                        ...S.statusPill,
                        background: badgeStyle.background,
                        color: badgeStyle.color,
                        borderColor: badgeStyle.borderColor,
                      }}
                    >
                      {formatStatusLabel(item.status)}
                    </span>
                  </div>

                  <div style={S.modalMeta}>
                    <span>{item.actor}</span>
                    <span>{formatDateTime(item.timestamp)}</span>
                    <span>{formatStatusLabel(item.source)}</span>
                  </div>

                  {item.contact_side ? (
                    <div style={S.historyStatusWrap}>
                      <span style={S.historySidePill}>
                        {formatContactSideLabel(
                          item.contact_side,
                          {
                            profileId: item.from_profile_id,
                            profileNo: item.from_profile_no,
                            profileName: item.from_profile_name,
                          },
                          {
                            profileId: item.to_profile_id,
                            profileNo: item.to_profile_no,
                            profileName: item.to_profile_name,
                          }
                        )}
                      </span>
                    </div>
                  ) : null}

                  <div style={S.notePanel}>
                    <div style={S.noteLabel}>Details</div>
                    <div style={S.noteValue}>{item.note}</div>
                  </div>

                  <div style={S.modalMeta}>
                    <span>
                      Recommendation date{" "}
                      {formatDateTime(item.recommendation_created_at)}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function RecommendationDashboard({
  token,
  user = null,
  onViewProfile = null,
  canViewProfile = false,
}) {
  const [report, setReport] = useState(EMPTY_REPORT);
  const [filters, setFilters] = useState({
    recommendedBy: "all",
    profileId: "all",
    recommendationStatus: "all",
    dateFrom: "",
    dateTo: "",
    q: "",
  });
  const [targetProfileView, setTargetProfileView] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [historyModal, setHistoryModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    items: [],
    loading: false,
    error: "",
  });
  const [stageDetailsModal, setStageDetailsModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    items: [],
  });

  const reportPolicy = useMemo(
    () => getPolicySummary(user, "reports", "view"),
    [user]
  );

  const fetchReport = async (nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (nextFilters.recommendedBy !== "all") {
        params.set("recommended_by", nextFilters.recommendedBy);
      }
      if (nextFilters.profileId !== "all") {
        params.set("from_profile_id", nextFilters.profileId);
      }
      if (nextFilters.recommendationStatus !== "all") {
        params.set("recommendation_status", nextFilters.recommendationStatus);
      }
      if (nextFilters.dateFrom) params.set("date_from", nextFilters.dateFrom);
      if (nextFilters.dateTo) params.set("date_to", nextFilters.dateTo);
      params.set("limit", "120");

      const res = await API.get(
        `/reports/recommendations-dashboard?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReport({
        ...EMPTY_REPORT,
        ...(res.data || {}),
        overview: {
          ...EMPTY_REPORT.overview,
          ...(res.data?.overview || {}),
        },
        staff: Array.isArray(res.data?.staff) ? res.data.staff : [],
        profiles: Array.isArray(res.data?.profiles) ? res.data.profiles : [],
        profile_options: Array.isArray(res.data?.profile_options)
          ? res.data.profile_options
          : [],
        target_breakdowns: Array.isArray(res.data?.target_breakdowns)
          ? res.data.target_breakdowns
          : [],
        activity: Array.isArray(res.data?.activity) ? res.data.activity : [],
        policy: {
          ...EMPTY_REPORT.policy,
          ...(res.data?.policy || {}),
        },
      });
    } catch (err) {
      console.error("Recommendation dashboard load failed", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load recommendation dashboard."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async ({
    title,
    subtitle,
    recommendedBy = filters.recommendedBy,
    profileId = filters.profileId,
  }) => {
    try {
      setHistoryModal({
        open: true,
        title,
        subtitle,
        items: [],
        loading: true,
        error: "",
      });

      const params = new URLSearchParams();
      if (recommendedBy !== "all") params.set("recommended_by", recommendedBy);
      if (profileId !== "all") params.set("from_profile_id", profileId);
      if (filters.recommendationStatus !== "all") {
        params.set("recommendation_status", filters.recommendationStatus);
      }
      if (filters.dateFrom) params.set("date_from", filters.dateFrom);
      if (filters.dateTo) params.set("date_to", filters.dateTo);
      params.set("limit", "150");

      const res = await API.get(
        `/reports/recommendations-dashboard?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setHistoryModal((prev) => ({
        ...prev,
        items: Array.isArray(res.data?.activity) ? res.data.activity : [],
        loading: false,
        error: "",
      }));
    } catch (err) {
      console.error("Recommendation history load failed", err);
      setHistoryModal((prev) => ({
        ...prev,
        loading: false,
        error:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load recommendation history.",
      }));
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStaff = useMemo(() => {
    if (filters.recommendedBy === "all") return null;
    return (
      report.staff.find(
        (item) => String(item.user_id) === String(filters.recommendedBy)
      ) || null
    );
  }, [filters.recommendedBy, report.staff]);

  const selectedProfile = useMemo(() => {
    if (filters.profileId === "all") return null;
    return (
      report.profiles.find(
        (item) => String(item.profile_id) === String(filters.profileId)
      ) || null
    );
  }, [filters.profileId, report.profiles]);

  const selectedProfileTargets = useMemo(() => {
    if (!selectedProfile) return [];
    return report.target_breakdowns.filter(
      (item) => String(item.profile_id) === String(selectedProfile.profile_id)
    );
  }, [report.target_breakdowns, selectedProfile]);

  const selectedTargetBreakdown = useMemo(() => {
    if (!selectedProfile || targetProfileView === "all") return null;
    return (
      selectedProfileTargets.find(
        (item) => String(item.to_profile_id) === String(targetProfileView)
      ) || null
    );
  }, [selectedProfile, selectedProfileTargets, targetProfileView]);

  const selectedProfileSummary = selectedTargetBreakdown || selectedProfile;

  const openStageDetails = (stageKey, label) => {
    if (!selectedProfile) return;
    const routeLabel =
      targetProfileView === "all"
        ? buildProfileLabel(selectedProfile.profile_no, selectedProfile.profile_name)
        : `${buildProfileLabel(
            selectedProfile.profile_no,
            selectedProfile.profile_name
          )} -> ${buildProfileLabel(
            selectedTargetBreakdown?.to_profile_no,
            selectedTargetBreakdown?.to_profile_name
          )}`;

    setStageDetailsModal({
      open: true,
      title: `${label} entries`,
      subtitle: routeLabel,
      items: buildStageDetailEntries(
        report.activity,
        selectedProfile,
        targetProfileView,
        stageKey
      ),
    });
  };

  const topPerformer = selectedStaff || report.staff[0] || null;
  const hasPolicyNotice = Boolean(
    Number(report.policy?.max_results) > 0 || reportPolicy?.denied
  );

  useEffect(() => {
    if (filters.profileId === "all") return;
    const hasSelectedProfile = report.profile_options.some(
      (item) => String(item.profile_id) === String(filters.profileId)
    );
    if (!hasSelectedProfile) {
      setFilters((prev) => ({ ...prev, profileId: "all" }));
    }
  }, [filters.profileId, report.profile_options]);

  useEffect(() => {
    if (!selectedProfile) {
      setTargetProfileView("all");
      return;
    }
    if (targetProfileView === "all") return;
    const hasSelectedTarget = selectedProfileTargets.some(
      (item) => String(item.to_profile_id) === String(targetProfileView)
    );
    if (!hasSelectedTarget) {
      setTargetProfileView("all");
    }
  }, [selectedProfile, selectedProfileTargets, targetProfileView]);

  if (loading) {
    return (
      <div style={S.center}>
        <div style={S.spinner} />
        <p style={S.loadingText}>Loading recommendation dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={S.center}>
        <div style={S.errorIcon}>!</div>
        <h3 style={S.errorTitle}>Could not load recommendation dashboard</h3>
        <p style={S.errorText}>{error}</p>
        <button style={S.primaryBtn} onClick={() => fetchReport()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={S.wrap}>
        <div style={S.hero}>
          <div>
            <div style={S.eyebrow}>Reports</div>
            <h1 style={S.pageTitle}>Recommendation Dashboard</h1>
            <p style={S.pageSub}>
              Review who is sharing the most recommendations, drill down into
              each source profile, and open recommendation history only when you
              need it.
            </p>
          </div>
          <button style={S.primaryBtn} onClick={() => fetchReport()}>
            Refresh
          </button>
        </div>

        {hasPolicyNotice && (
          <div style={S.policyNotice}>
            <strong style={S.policyNoticeTitle}>
              Report access policy is active.
            </strong>
            <div style={S.policyNoticeBody}>
              {Number(report.policy?.max_results) > 0 && (
                <span>
                  Recommendation activity is capped at{" "}
                  {report.policy.max_results} rows per request.
                </span>
              )}
              {reportPolicy?.denied && (
                <span>Some report visibility is restricted by policy.</span>
              )}
            </div>
          </div>
        )}

        <div style={S.metricGrid}>
          <MetricCard
            label="Recommendations"
            value={report.overview.total_recommendations}
            hint="Manual shares logged"
          />
          <MetricCard
            label="Matched"
            value={report.overview.matched_recommendations}
            hint="Successful recommendation outcomes"
          />
          <MetricCard
            label="Unmatched"
            value={report.overview.unmatched_recommendations}
            hint="Rejected or cancelled outcomes"
          />
          <MetricCard
            label="Active"
            value={report.overview.active_recommendations}
            hint="Still in progress"
          />
        </div>

        <div style={S.filtersCard}>
          <div style={S.searchBlock}>
            <label style={S.filterLabel}>Search</label>
            <div style={S.searchBox}>
              <span style={S.searchIcon}>⌕</span>
              <input
                style={S.searchInput}
                value={filters.q}
                onChange={(event) =>
                  setFilters((prev) => ({ ...prev, q: event.target.value }))
                }
                placeholder="Search user, profile no, profile name, note or outcome..."
              />
            </div>
          </div>

          <div style={S.controlsRow}>
            <div style={S.filterSlots}>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}>User</label>
                <select
                  style={S.select}
                  value={filters.recommendedBy}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      recommendedBy: event.target.value,
                      profileId: "all",
                    }))
                  }
                >
                  <option value="all">All Users</option>
                  {report.staff.map((item) => (
                    <option key={item.user_id} value={String(item.user_id)}>
                      {item.user_email}
                    </option>
                  ))}
                </select>
              </div>

              {filters.recommendedBy !== "all" && (
                <div style={S.profileFilterGroup}>
                  <label style={S.filterLabel}>Profile</label>
                  <select
                    style={S.select}
                    value={filters.profileId}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        profileId: event.target.value,
                      }))
                    }
                    >
                    <option value="all">All Profiles</option>
                    {report.profile_options.map((item) => (
                      <option key={item.profile_id} value={String(item.profile_id)}>
                        {buildProfileLabel(item.profile_no, item.profile_name)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={S.filterGroup}>
                <label style={S.filterLabel}>Stage</label>
                <select
                  style={S.select}
                  value={filters.recommendationStatus}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      recommendationStatus: event.target.value,
                    }))
                  }
                >
                  {RECOMMENDATION_STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={S.filterGroup}>
                <label style={S.filterLabel}>From</label>
                <input
                  style={S.input}
                  type="date"
                  value={filters.dateFrom}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))
                  }
                />
              </div>

              <div style={S.filterGroup}>
                <label style={S.filterLabel}>To</label>
                <input
                  style={S.input}
                  type="date"
                  value={filters.dateTo}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, dateTo: event.target.value }))
                  }
                />
              </div>
            </div>

            <div style={S.actionBar}>
              <button style={S.primaryBtn} onClick={() => fetchReport(filters)}>
                Apply
              </button>
              <button
                style={S.clearBtn}
                onClick={() => {
                  const nextFilters = {
                    recommendedBy: "all",
                    profileId: "all",
                    recommendationStatus: "all",
                    dateFrom: "",
                    dateTo: "",
                    q: "",
                  };
                  setFilters(nextFilters);
                  fetchReport(nextFilters);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {topPerformer && (
          <div style={S.highlightCard}>
            <div>
              <div style={S.highlightEyebrow}>
                {selectedStaff ? "Selected recommender" : "Top recommender"}
              </div>
              <h3 style={S.highlightTitle}>{topPerformer.user_email}</h3>
              <p style={S.highlightCopy}>
                {topPerformer.total_recommendations} recommendations logged,
                {` ${topPerformer.matched_recommendations} matched`} and
                {` ${topPerformer.unmatched_recommendations} unmatched`}.
              </p>
            </div>
            <div style={S.highlightStats}>
              <span style={S.highlightPill}>
                Conversion{" "}
                {formatPercent(
                  (Number(topPerformer.matched_recommendations || 0) /
                    Math.max(Number(topPerformer.total_recommendations || 0), 1)) *
                    100
                )}
              </span>
              <span style={S.highlightMeta}>
                Last recommendation{" "}
                {formatDateTime(topPerformer.last_recommendation_at)}
              </span>
            </div>
          </div>
        )}

        <section style={S.sectionCard}>
          <div style={S.sectionHead}>
            <div>
              <div style={S.sectionEyebrow}>Team view</div>
              <h3 style={S.sectionTitle}>User recommendation performance</h3>
            </div>
          </div>

          {report.staff.length === 0 ? (
            <div style={S.emptyState}>
              No recommendation activity found for the selected filters.
            </div>
          ) : (
            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>User</th>
                    <th style={S.th}>Total</th>
                    <th style={S.th}>Matched</th>
                    <th style={S.th}>Unmatched</th>
                    <th style={S.th}>Active</th>
                    <th style={S.th}>Conversion</th>
                    <th style={S.th}>Last Activity</th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {report.staff.map((item) => {
                    const total = Number(item.total_recommendations || 0);
                    const matched = Number(item.matched_recommendations || 0);
                    return (
                      <tr key={item.user_id}>
                        <td style={S.tdStrong}>{item.user_email}</td>
                        <td style={S.td}>{item.total_recommendations}</td>
                        <td style={S.td}>{item.matched_recommendations}</td>
                        <td style={S.td}>{item.unmatched_recommendations}</td>
                        <td style={S.td}>{item.active_recommendations}</td>
                        <td style={S.td}>
                          {formatPercent((matched / Math.max(total, 1)) * 100)}
                        </td>
                        <td style={S.td}>
                          {formatDateTime(item.last_recommendation_at)}
                        </td>
                        <td style={S.td}>
                          <div style={S.rowActions}>
                            <GhostAction
                              onClick={() => {
                                const nextFilters = {
                                  ...filters,
                                  recommendedBy: String(item.user_id),
                                  profileId: "all",
                                };
                                setFilters(nextFilters);
                                fetchReport(nextFilters);
                              }}
                            >
                              Focus
                            </GhostAction>
                            <GhostAction
                              onClick={() =>
                                fetchHistory({
                                  title: item.user_email,
                                  subtitle: "Recommendation history for this user",
                                  recommendedBy: String(item.user_id),
                                  profileId: "all",
                                })
                              }
                            >
                              History
                            </GhostAction>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {filters.recommendedBy !== "all" && (
          <section style={S.sectionCard}>
            <div style={S.sectionHead}>
              <div>
                <div style={S.sectionEyebrow}>Profile drill-down</div>
                <h3 style={S.sectionTitle}>Profile-wise recommendation counts</h3>
              </div>
              <div style={S.sectionMeta}>
                {report.profiles.length} source profile
                {report.profiles.length !== 1 ? "s" : ""} for this user
              </div>
            </div>

            {selectedProfile ? (
              <div style={S.profileSummaryWrap}>
                <div style={S.profileSummaryCard}>
                  <div style={S.profileSummaryEyebrow}>
                    {selectedTargetBreakdown ? "Selected route" : "Selected profile"}
                  </div>
                  <div style={S.profileRouteHead}>
                    <span style={S.profileRouteText}>
                      {buildProfileLabel(
                        selectedProfile.profile_no,
                        selectedProfile.profile_name
                      )}
                    </span>
                    <span style={S.profileRouteArrow}>{"\u2192"}</span>
                    <div style={S.profileRouteSelectWrap}>
                      <select
                        style={S.profileRouteSelect}
                        value={targetProfileView}
                        onChange={(event) => setTargetProfileView(event.target.value)}
                      >
                        <option value="all">All</option>
                        {selectedProfileTargets.map((item) => (
                          <option key={item.to_profile_id} value={String(item.to_profile_id)}>
                            {buildProfileLabel(item.to_profile_no, item.to_profile_name)}
                          </option>
                        ))}
                      </select>
                      <span style={S.profileRouteChevron}>{"\u25BE"}</span>
                    </div>
                  </div>
                  <div style={S.profileSummaryMeta}>
                    Last activity{" "}
                    {formatDateTime(
                      selectedProfileSummary?.last_activity_at ||
                        selectedProfileSummary?.last_recommendation_at
                    )}
                  </div>
                  <div style={S.profileSummaryMeta}>
                    Recommendation date{" "}
                    {formatDateTime(selectedProfileSummary?.last_recommendation_at)}
                  </div>
                </div>
                <div style={S.profileMetricGrid}>
                  <MetricCard
                    label="Total"
                    value={selectedProfileSummary?.total_recommendations || 0}
                    hint="Recommendations from this profile"
                  />
                  <MetricCard
                    label="Matched"
                    value={selectedProfileSummary?.matched_recommendations || 0}
                    hint="Converted outcomes"
                  />
                  <MetricCard
                    label="Unmatched"
                    value={selectedProfileSummary?.unmatched_recommendations || 0}
                    hint="Rejected or cancelled"
                  />
                  <MetricCard
                    label="Active"
                    value={selectedProfileSummary?.active_recommendations || 0}
                    hint="Still in progress"
                  />
                </div>
                <div style={S.stageMetricGrid}>
                  {PROFILE_STAGE_METRICS.map((metric) => (
                    <button
                      key={metric.key}
                      type="button"
                      style={S.stageMetricButton}
                      onClick={() => openStageDetails(metric.key, metric.label)}
                    >
                      <div style={S.stageMetricCard}>
                        <div style={S.stageMetricLabel}>{metric.label}</div>
                        <div style={S.stageMetricValue}>
                          {selectedProfileSummary?.[metric.field] || 0}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {report.profiles.length === 0 ? (
              <div style={S.emptyState}>
                No source profiles found for this user and filter selection.
              </div>
            ) : (
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Profile</th>
                      <th style={S.th}>Total</th>
                      <th style={S.th}>Matched</th>
                      <th style={S.th}>Unmatched</th>
                      <th style={S.th}>Active</th>
                      <th style={S.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.profiles.map((item) => (
                      <tr key={item.profile_id}>
                        <td style={S.tdStrong}>
                          {buildProfileLabel(item.profile_no, item.profile_name)}
                        </td>
                        <td style={S.td}>{item.total_recommendations}</td>
                        <td style={S.td}>{item.matched_recommendations}</td>
                        <td style={S.td}>{item.unmatched_recommendations}</td>
                        <td style={S.td}>{item.active_recommendations}</td>
                        <td style={S.td}>
                          <div style={S.rowActions}>
                            <GhostAction
                              onClick={() => {
                                const nextFilters = {
                                  ...filters,
                                  profileId: String(item.profile_id),
                                };
                                setFilters(nextFilters);
                                fetchReport(nextFilters);
                              }}
                            >
                              Focus
                            </GhostAction>
                            <GhostAction
                              onClick={() =>
                                fetchHistory({
                                  title: buildProfileLabel(
                                    item.profile_no,
                                    item.profile_name
                                  ),
                                  subtitle: "Recommendation history for this source profile",
                                  recommendedBy: filters.recommendedBy,
                                  profileId: String(item.profile_id),
                                })
                              }
                            >
                              History
                            </GhostAction>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <HistoryModal
        open={historyModal.open}
        title={historyModal.title}
        subtitle={historyModal.subtitle}
        items={historyModal.items}
        loading={historyModal.loading}
        error={historyModal.error}
        onClose={() =>
          setHistoryModal({
            open: false,
            title: "",
            subtitle: "",
            items: [],
            loading: false,
            error: "",
          })
        }
        canViewProfile={canViewProfile}
        onViewProfile={onViewProfile}
      />
      <StageDetailsModal
        open={stageDetailsModal.open}
        title={stageDetailsModal.title}
        subtitle={stageDetailsModal.subtitle}
        items={stageDetailsModal.items}
        onClose={() =>
          setStageDetailsModal({
            open: false,
            title: "",
            subtitle: "",
            items: [],
          })
        }
        canViewProfile={canViewProfile}
        onViewProfile={onViewProfile}
      />
    </div>
  );
}

const S = {
  page: {
    minHeight: "100%",
    padding: "28px 18px 42px",
  },
  wrap: {
    maxWidth: 1220,
    margin: "0 auto",
    animation: "fadeUp 0.28s ease both",
  },
  hero: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 18,
    padding: 24,
    borderRadius: 30,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.97) 0%, rgba(255,247,245,0.95) 100%)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 20px 60px ${BRAND.shadow}`,
    marginBottom: 18,
  },
  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2.8,
    color: BRAND.mix,
    fontWeight: 900,
    marginBottom: 8,
  },
  pageTitle: {
    margin: 0,
    color: BRAND.black,
    fontSize: 32,
    fontWeight: 950,
    letterSpacing: -0.8,
  },
  pageSub: {
    margin: "8px 0 0",
    maxWidth: 760,
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  primaryBtn: {
    border: "none",
    borderRadius: 999,
    padding: "14px 24px",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    background: "linear-gradient(135deg, #D73C2E, #B9343A)",
    color: "#fff",
    boxShadow: "0 18px 34px rgba(215,60,46,0.18)",
  },
  clearBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 999,
    padding: "13px 18px",
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
    background: "#fff",
    color: BRAND.black,
  },
  ghostAction: {
    border: "none",
    background: "transparent",
    color: BRAND.red,
    fontWeight: 800,
    cursor: "pointer",
    padding: 0,
    fontSize: 13,
  },
  policyNotice: {
    display: "grid",
    gap: 6,
    padding: "16px 18px",
    marginBottom: 18,
    borderRadius: 24,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
  },
  policyNoticeTitle: {
    color: BRAND.black,
  },
  policyNoticeBody: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    color: BRAND.muted,
    fontSize: 13,
  },
  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 14,
    marginBottom: 18,
  },
  metricCard: {
    padding: "18px 18px 16px",
    borderRadius: 24,
    background:
      "linear-gradient(180deg, rgba(255,247,245,0.92) 0%, rgba(255,255,255,0.98) 100%)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 14px 28px ${BRAND.shadow}`,
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  metricValue: {
    marginTop: 10,
    fontSize: 34,
    fontWeight: 950,
    color: BRAND.black,
    lineHeight: 1,
  },
  metricHint: {
    marginTop: 8,
    fontSize: 13,
    color: BRAND.muted,
    lineHeight: 1.5,
  },
  filtersCard: {
    display: "grid",
    gap: 16,
    padding: 20,
    borderRadius: 30,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 18px 42px ${BRAND.shadow}`,
    marginBottom: 18,
  },
  searchBlock: {
    display: "none",
    gap: 8,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "0 18px",
    borderRadius: 20,
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.redSoftAlt,
    minHeight: 58,
  },
  searchIcon: {
    color: BRAND.mutedSoft,
    fontSize: 16,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 15,
    color: BRAND.black,
  },
  filterGroup: {
    display: "grid",
    gap: 6,
    minWidth: 0,
  },
  profileFilterGroup: {
    display: "grid",
    gap: 6,
    minWidth: 0,
    gridColumn: "span 2",
  },
  controlsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
    alignItems: "end",
    justifyContent: "space-between",
  },
  filterSlots: {
    flex: "1 1 720px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 14,
  },
  filterLabel: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  select: {
    width: "100%",
    minHeight: 52,
    borderRadius: 18,
    border: `1px solid ${BRAND.borderStrong}`,
    padding: "0 14px",
    fontSize: 14,
    color: BRAND.black,
    background: "#fff",
    outline: "none",
  },
  input: {
    minHeight: 52,
    borderRadius: 18,
    border: `1px solid ${BRAND.borderStrong}`,
    padding: "0 14px",
    fontSize: 14,
    color: BRAND.black,
    background: "#fff",
    outline: "none",
  },
  actionBar: {
    display: "flex",
    gap: 10,
    justifyContent: "flex-end",
    alignItems: "center",
    flex: "0 0 auto",
  },
  highlightCard: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 18,
    padding: 22,
    borderRadius: 28,
    marginBottom: 18,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,242,239,0.95) 100%)",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 18px 42px ${BRAND.shadow}`,
  },
  highlightEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2.2,
    color: BRAND.mix,
    fontWeight: 900,
    marginBottom: 8,
  },
  highlightTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 950,
    color: BRAND.black,
  },
  highlightCopy: {
    margin: "8px 0 0",
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  highlightStats: {
    display: "grid",
    gap: 8,
    justifyItems: "end",
  },
  highlightPill: {
    padding: "10px 14px",
    borderRadius: 999,
    background: BRAND.greenSoft,
    color: BRAND.green,
    fontSize: 13,
    fontWeight: 800,
  },
  highlightMeta: {
    color: BRAND.muted,
    fontSize: 13,
  },
  sectionCard: {
    padding: 22,
    borderRadius: 28,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 18px 42px ${BRAND.shadow}`,
    marginBottom: 18,
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 16,
    marginBottom: 18,
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2.2,
    textTransform: "uppercase",
    color: BRAND.mix,
    marginBottom: 8,
  },
  sectionTitle: {
    margin: 0,
    color: BRAND.black,
    fontSize: 24,
    fontWeight: 900,
  },
  sectionMeta: {
    fontSize: 13,
    color: BRAND.muted,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    color: BRAND.muted,
    padding: "0 0 14px",
    borderBottom: `1px solid ${BRAND.border}`,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "14px 0",
    borderBottom: `1px solid rgba(185,52,58,0.08)`,
    fontSize: 14,
    color: BRAND.muted,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  },
  tdStrong: {
    padding: "14px 0",
    borderBottom: `1px solid rgba(185,52,58,0.08)`,
    fontSize: 14,
    color: BRAND.black,
    fontWeight: 800,
    whiteSpace: "nowrap",
    verticalAlign: "top",
  },
  rowActions: {
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  profileSummaryWrap: {
    display: "grid",
    gap: 14,
    marginBottom: 18,
  },
  profileSummaryCard: {
    padding: 18,
    borderRadius: 22,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
  },
  profileSummaryEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2.2,
    textTransform: "uppercase",
    color: BRAND.mix,
    marginBottom: 8,
  },
  profileSummaryTitle: {
    margin: 0,
    color: BRAND.black,
    fontSize: 22,
    fontWeight: 900,
  },
  profileSummaryMeta: {
    marginTop: 8,
    color: BRAND.muted,
    fontSize: 13,
  },
  profileRouteHead: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  profileRouteText: {
    color: BRAND.black,
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1.2,
  },
  profileRouteArrow: {
    color: BRAND.mutedSoft,
    fontSize: 22,
    fontWeight: 900,
    lineHeight: 1,
  },
  profileRouteSelectWrap: {
    position: "relative",
    minWidth: 240,
    flex: "1 1 320px",
    maxWidth: 520,
  },
  profileRouteSelect: {
    width: "100%",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    padding: "0 44px 0 16px",
    height: 48,
    borderRadius: 16,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.black,
    fontSize: 16,
    fontWeight: 800,
    outline: "none",
    cursor: "pointer",
    boxSizing: "border-box",
  },
  profileRouteChevron: {
    position: "absolute",
    right: 16,
    top: "50%",
    transform: "translateY(-50%)",
    color: BRAND.mutedSoft,
    fontSize: 16,
    pointerEvents: "none",
    fontWeight: 900,
    lineHeight: 1,
  },
  profileMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 12,
  },
  stageMetricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(132px, 1fr))",
    gap: 10,
  },
  stageMetricButton: {
    padding: 0,
    margin: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },
  stageMetricCard: {
    padding: "14px 14px 12px",
    borderRadius: 18,
    border: `1px solid ${BRAND.border}`,
    background: "linear-gradient(180deg, rgba(255,250,249,0.95) 0%, rgba(255,255,255,0.98) 100%)",
    transition: "transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease",
    boxShadow: "0 10px 24px rgba(20,24,35,0.04)",
  },
  stageMetricLabel: {
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1.4,
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  stageMetricValue: {
    marginTop: 8,
    fontSize: 28,
    fontWeight: 900,
    color: BRAND.black,
    lineHeight: 1,
  },
  emptyState: {
    padding: 22,
    borderRadius: 20,
    background: BRAND.redSoftAlt,
    border: `1px dashed ${BRAND.borderStrong}`,
    color: BRAND.muted,
    fontSize: 14,
    textAlign: "center",
  },
  center: {
    minHeight: "60vh",
    display: "grid",
    placeItems: "center",
    textAlign: "center",
    gap: 14,
    padding: 24,
  },
  spinner: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    border: "4px solid rgba(185,52,58,0.15)",
    borderTopColor: BRAND.red,
    animation: "spin 0.8s linear infinite",
  },
  loadingText: {
    margin: 0,
    color: BRAND.muted,
    fontSize: 14,
  },
  errorIcon: {
    width: 54,
    height: 54,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    background: BRAND.redSoft,
    color: BRAND.red,
    fontSize: 24,
    fontWeight: 900,
  },
  errorTitle: {
    margin: 0,
    color: BRAND.black,
    fontSize: 24,
    fontWeight: 900,
  },
  errorText: {
    margin: 0,
    maxWidth: 560,
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(20, 24, 35, 0.42)",
    backdropFilter: "blur(8px)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 2000,
  },
  modalCard: {
    width: "min(980px, 100%)",
    maxHeight: "85vh",
    overflow: "hidden",
    borderRadius: 28,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 28px 80px rgba(20,24,35,0.22)",
    display: "grid",
    gridTemplateRows: "auto 1fr",
  },
  modalHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    padding: 22,
    borderBottom: `1px solid ${BRAND.border}`,
    background: "linear-gradient(180deg, rgba(255,247,245,0.94) 0%, rgba(255,255,255,0.98) 100%)",
  },
  modalEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 2.2,
    textTransform: "uppercase",
    color: BRAND.mix,
    marginBottom: 8,
  },
  modalTitle: {
    margin: 0,
    color: BRAND.black,
    fontSize: 24,
    fontWeight: 900,
  },
  modalSub: {
    marginTop: 8,
    color: BRAND.muted,
    fontSize: 13,
  },
  modalClose: {
    border: "none",
    background: BRAND.redSoftAlt,
    color: BRAND.black,
    width: 42,
    height: 42,
    borderRadius: "50%",
    fontSize: 26,
    cursor: "pointer",
    lineHeight: 1,
  },
  modalState: {
    padding: 28,
    color: BRAND.muted,
    fontSize: 14,
    textAlign: "center",
  },
  modalError: {
    padding: 28,
    color: BRAND.red,
    fontSize: 14,
    textAlign: "center",
  },
  modalList: {
    overflowY: "auto",
    padding: 22,
    display: "grid",
    gap: 14,
    background: BRAND.panel,
  },
  modalItem: {
    padding: 18,
    borderRadius: 22,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
  },
  modalItemHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
  },
  modalProfileRoute: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  modalMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 10,
    fontSize: 13,
    color: BRAND.muted,
  },
  profileBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    color: BRAND.black,
    fontSize: 16,
    fontWeight: 900,
    cursor: "pointer",
    textAlign: "left",
  },
  profileText: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    color: BRAND.black,
    fontSize: 16,
    fontWeight: 900,
    textAlign: "left",
  },
  arrow: {
    color: BRAND.mutedSoft,
    fontWeight: 900,
  },
  statusPill: {
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  },
  notePanel: {
    marginTop: 16,
    padding: 14,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
  },
  noteLabel: {
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.8,
    color: BRAND.muted,
    marginBottom: 8,
  },
  noteValue: {
    color: BRAND.black,
    fontSize: 15,
    lineHeight: 1.6,
  },
  historyBlock: {
    marginTop: 16,
    display: "grid",
    gap: 10,
  },
  historyTitle: {
    fontSize: 12,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 1.8,
    color: BRAND.muted,
  },
  historyRow: {
    padding: 12,
    borderRadius: 16,
    background: "#fff",
    border: `1px solid rgba(185,52,58,0.10)`,
  },
  historyTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },
  historyStatusWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  historyStatus: {
    fontWeight: 800,
    color: BRAND.black,
    fontSize: 14,
  },
  historySidePill: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 24,
    padding: "0 9px",
    borderRadius: 999,
    background: BRAND.redSoftAlt,
    border: `1px solid ${BRAND.border}`,
    color: BRAND.muted,
    fontSize: 11,
    fontWeight: 800,
  },
  historyDate: {
    color: BRAND.muted,
    fontSize: 12,
  },
  historyByline: {
    marginTop: 6,
    fontSize: 12,
    color: BRAND.muted,
  },
  historyNote: {
    marginTop: 8,
    color: BRAND.black,
    fontSize: 14,
    lineHeight: 1.55,
  },
  historyEmpty: {
    padding: 12,
    borderRadius: 16,
    background: "#fff",
    border: `1px dashed ${BRAND.borderStrong}`,
    color: BRAND.muted,
    fontSize: 13,
  },
};
